// beaconTypes.ts — types, helpers and API calls for Sheet Beacons.
// Mirrors models/sheetBeacon.go on the backend.

import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { shortId } from '../../SheetsManager/parts/SheetImportDetailsModal';

// ── Types ─────────────────────────────────────────────────────────────────────
export type BeaconState = 'idle' | 'checking' | 'ok' | 'error';
export type PingStatus = 'ok' | 'error';
export type PingTrigger = 'schedule' | 'manual' | 'setup';

export interface UserRef {
  id: string;
  email?: string | null;
  role?: string | null;
}

export interface PingLite {
  status: PingStatus;
  new_rows: number;
  checked_at: string;
}

export interface SheetBeacon {
  id: string;
  agency_id: string;
  sheet_id: string;
  sheet_url: string;
  display_name?: string;
  interval_minutes: number;
  is_active: boolean;
  state: BeaconState;
  baseline_set: boolean;
  last_row_count: number;
  unseen_new_rows: number;
  checking_since?: string | null;
  last_checked_at?: string | null;
  next_check_at?: string | null;
  last_new_rows_at?: string | null;
  last_error?: string;
  consecutive_failures: number;
  total_pings: number;
  recent_pings: PingLite[] | null;
  created_by?: UserRef | null;
  created_at: string;
  updated_at: string;
}

export interface BeaconPing {
  id: string;
  beacon_id: string;
  agency_id: string;
  sheet_id: string;
  trigger: PingTrigger;
  status: PingStatus;
  total_rows: number;
  new_rows: number;
  duration_ms: number;
  error?: string;
  checked_by?: UserRef | null;
  checked_at: string;
}

export interface BeaconSummary {
  total: number;
  active: number;
  errored: number;
  unseen_new_rows: number;
  last_ping_at?: string | null;
}

export interface PageInfo {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// ── API ───────────────────────────────────────────────────────────────────────
interface ListBeaconsRes {
  success: boolean;
  data: SheetBeacon[];
  summary: BeaconSummary;
  server_time: string;
}
interface BeaconRes {
  success: boolean;
  data: SheetBeacon;
  ping?: BeaconPing | null;
  message?: string;
  warning?: string;
  /** Ping now: why the sheet check failed (sent as check_error, not error — see axiosInterceptor). */
  check_error?: string;
}
interface PingsRes {
  success: boolean;
  data: BeaconPing[];
  pagination: PageInfo;
}

// Create and Ping now read the Google Sheet before answering; the server
// allows up to 60s for that, so don't let the default 10s client timeout cut it off.
const SHEET_READ_TIMEOUT_MS = 70000;

export const beaconApi = {
  list: () => apiClient.get<ListBeaconsRes>('/v1/sheet-beacons').then((r) => r.data),
  create: (body: { sheet_url: string; display_name?: string; interval_minutes: number }) =>
    apiClient.post<BeaconRes>('/v1/sheet-beacons', body, { timeout: SHEET_READ_TIMEOUT_MS }).then((r) => r.data),
  update: (id: string, body: Partial<{ display_name: string; interval_minutes: number; is_active: boolean }>) =>
    apiClient.patch<BeaconRes>(`/v1/sheet-beacons/${id}`, body).then((r) => r.data),
  ping: (id: string) =>
    apiClient.post<BeaconRes>(`/v1/sheet-beacons/${id}/ping`, undefined, { timeout: SHEET_READ_TIMEOUT_MS }).then((r) => r.data),
  seen: (id: string) => apiClient.post<BeaconRes>(`/v1/sheet-beacons/${id}/seen`).then((r) => r.data),
  remove: (id: string) => apiClient.delete(`/v1/sheet-beacons/${id}`).then((r) => r.data),
  pings: (params: URLSearchParams) => apiClient.get<PingsRes>(`/v1/sheet-beacons/pings?${params}`).then((r) => r.data),
};

export const apiError = (e: any, fallback: string): string => e?.response?.data?.error || e?.message || fallback;

/** How many recent checks the backend keeps on each beacon (models.RecentPingsKept). */
export const RECENT_PINGS_KEPT = 12;

// ── Intervals ─────────────────────────────────────────────────────────────────
// Keep in sync with models.BeaconIntervals.
export const INTERVAL_OPTIONS: { value: number; label: string; short: string }[] = [
  { value: 5, label: 'Every 5 minutes', short: '5 min' },
  { value: 15, label: 'Every 15 minutes', short: '15 min' },
  { value: 30, label: 'Every 30 minutes', short: '30 min' },
  { value: 60, label: 'Every hour', short: '1 hr' },
  { value: 180, label: 'Every 3 hours', short: '3 hr' },
  { value: 360, label: 'Every 6 hours', short: '6 hr' },
  { value: 720, label: 'Every 12 hours', short: '12 hr' },
  { value: 1440, label: 'Once a day', short: '24 hr' },
];
export const DEFAULT_INTERVAL = 15;

export const intervalShort = (m: number) => INTERVAL_OPTIONS.find((o) => o.value === m)?.short ?? `${m} min`;

// ── Naming ────────────────────────────────────────────────────────────────────
/** Display name if the user set one, otherwise the short spreadsheet ID. */
export const beaconName = (b: Pick<SheetBeacon, 'display_name' | 'sheet_id'>) => b.display_name?.trim() || shortId(b.sheet_id);

// ── Health: one word that drives every colour / animation ─────────────────────
export type Health = 'new' | 'live' | 'checking' | 'error' | 'paused' | 'idle';

export const healthOf = (b: SheetBeacon): Health => {
  if (!b.is_active) return 'paused';
  if (b.state === 'checking') return 'checking';
  if (b.state === 'error') return 'error';
  if (b.unseen_new_rows > 0) return 'new';
  if (!b.last_checked_at) return 'idle';
  return 'live';
};

export const HEALTH: Record<Health, { label: string; color: string; glow: string; text: string }> = {
  new: { label: 'New leads', color: '#c8e94b', glow: 'rgba(200,233,75,0.75)', text: '#4a5e00' },
  live: { label: 'Live', color: '#22c55e', glow: 'rgba(34,197,94,0.6)', text: '#15803d' },
  checking: { label: 'Checking', color: '#38bdf8', glow: 'rgba(56,189,248,0.65)', text: '#0369a1' },
  error: { label: 'Failing', color: '#ef4444', glow: 'rgba(239,68,68,0.6)', text: '#b91c1c' },
  paused: { label: 'Paused', color: '#9ca3af', glow: 'rgba(156,163,175,0)', text: '#4b5563' },
  idle: { label: 'Starting', color: '#a3a3a3', glow: 'rgba(163,163,163,0.4)', text: '#4b5563' },
};

// ── Time helpers ──────────────────────────────────────────────────────────────
export const fmtAgo = (iso: string | null | undefined, now: number): string => {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '—';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/** "4:05", "1h 12m", or "due" once the time has passed. */
export const fmtCountdown = (iso: string | null | undefined, now: number): string => {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - now;
  if (isNaN(ms)) return '—';
  if (ms <= 0) return 'due';
  const s = Math.ceil(ms / 1000);
  if (s < 3600) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

/** 0 → 1 progress from the last check to the next one. */
export const cycleProgress = (b: SheetBeacon, now: number): number => {
  if (!b.last_checked_at || !b.next_check_at) return 0;
  const a = new Date(b.last_checked_at).getTime();
  const z = new Date(b.next_check_at).getTime();
  if (z <= a) return 1;
  return Math.min(1, Math.max(0, (now - a) / (z - a)));
};
